import { Component } from '@theme/component';

/**
 * @typedef {Object} AxMulticolumnRefs
 * @property {HTMLUListElement} list
 * @property {HTMLElement & { swiper?: any; initialize?: () => void }} [swiper]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 */

/**
 * Ax Multicolumn — carrousel activable séparément sur ordinateur et mobile.
 *
 * Les tuiles passent de la grille au carrousel et retour, comme dans
 * `ax-reveal-cards` : deux conteneurs coexistent et le JS déplace les enfants au
 * franchissement du breakpoint. Garder un seul `<swiper-container>` pour les deux
 * rôles ne marche pas — il crée son shadow DOM dès l'insertion, et un shadow sans
 * slot actif n'affiche aucun enfant.
 *
 * @extends {Component<AxMulticolumnRefs>}
 */
class AxMulticolumn extends Component {
  requiredRefs = ['list'];

  /** @type {MediaQueryList | null} */
  #mql = null;
  #onChange = null;
  #monte = false;

  connectedCallback() {
    super.connectedCallback();
    if (!this.refs.swiper) return;

    this.#mql = window.matchMedia('(min-width: 750px)');
    this.#init();
  }

  async #init() {
    await customElements.whenDefined('swiper-container');
    if (!this.isConnected) return;

    this.#onChange = () => this.#sync();
    this.#mql?.addEventListener('change', this.#onChange);
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#onChange) this.#mql?.removeEventListener('change', this.#onChange);
    const sw = this.refs.swiper?.swiper;
    if (sw && !sw.destroyed) sw.destroy(true, true);
  }

  previous() {
    this.refs.swiper?.swiper?.slidePrev();
  }

  next() {
    this.refs.swiper?.swiper?.slideNext();
  }

  /** Monte ou démonte le carrousel selon le breakpoint et les réglages. */
  #sync() {
    const desktop = Boolean(this.#mql?.matches);
    const voulu = desktop ? this.dataset.carouselDesktop === 'true' : this.dataset.carouselMobile === 'true';

    if (voulu) this.#monter(desktop);
    else this.#demonter();
  }

  /** @param {boolean} desktop */
  #monter(desktop) {
    const { list, swiper } = this.refs;
    if (!list || !swiper) return;

    if (!this.#monte) {
      for (const li of [...list.children]) {
        const slide = document.createElement('swiper-slide');
        slide.className = li.className;
        while (li.firstChild) slide.appendChild(li.firstChild);
        swiper.appendChild(slide);
      }
      list.replaceChildren();
    }

    const options = {
      // En mobile, `slidesMobile` vaut 1,3 quand l'aperçu est demandé : c'est Swiper
      // qui calcule la largeur des slides (il l'écrit en style inline), donc le CSS
      // ne peut pas la piloter.
      slidesPerView: Number(desktop ? this.dataset.colsDesktop : this.dataset.slidesMobile) || 1,
      spaceBetween: Number(desktop ? this.dataset.gap : this.dataset.gapMobile) || 16,
      grabCursor: true,
      a11y: { enabled: true },
      watchOverflow: true,
    };

    // Après un passage en grille, `swiper.swiper` pointe encore sur l'instance
    // détruite : il faut la réinitialiser, pas la mettre à jour. D'où le test sur
    // `destroyed` — sans lui, `update()` serait appelé sur une instance morte et le
    // carrousel ne se recréerait pas au retour vers le breakpoint mobile.
    const existant = swiper.swiper;
    if (existant && !existant.destroyed) {
      Object.assign(existant.params, options);
      existant.update();
    } else {
      Object.assign(swiper, options);
      swiper.initialize?.();
      swiper.removeAttribute('init');
      this.#syncArrows();
    }

    this.#monte = true;
    this.removeAttribute('data-sans-navigation');
    document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));
  }

  #demonter() {
    const { list, swiper } = this.refs;
    if (!this.#monte || !list || !swiper) {
      this.setAttribute('data-sans-navigation', '');
      return;
    }

    const sw = swiper.swiper;
    if (sw && !sw.destroyed) sw.destroy(true, true);

    for (const slide of [...swiper.children]) {
      const li = document.createElement('li');
      li.className = slide.className.replace(/\bswiper-slide[\w-]*\b/g, '').trim();
      while (slide.firstChild) li.appendChild(slide.firstChild);
      list.appendChild(li);
    }
    swiper.replaceChildren();

    this.#monte = false;
    this.setAttribute('data-sans-navigation', '');
    document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));
  }

  /** Grise les flèches aux extrémités. */
  #syncArrows() {
    const swiper = this.refs.swiper?.swiper;
    const { previous, next } = this.refs;
    if (!swiper || (!previous && !next)) return;

    const sync = () => {
      if (previous) previous.disabled = Boolean(swiper.isBeginning);
      if (next) next.disabled = Boolean(swiper.isEnd);
    };
    sync();

    for (const evenement of ['slideChange', 'reachBeginning', 'reachEnd', 'fromEdge', 'resize']) {
      swiper.on(evenement, sync);
    }
  }
}

if (!customElements.get('ax-multicolumn')) {
  customElements.define('ax-multicolumn', AxMulticolumn);
}
